/* eslint-disable sonarjs/cognitive-complexity */
import ClockIcon from 'assets/icons/clock-new.svg';
import EnergyIcon from 'assets/icons/energy.svg';
import HeartIcon from 'assets/icons/heart.svg';
import LockIcon from 'assets/icons/lock-new.svg';
import Pending from 'assets/icons/pending.svg';
import CheckMark from 'assets/icons/circleCheck.svg';
import InfoAlert from 'assets/icons/info-alert.svg';
import Button from 'components/Button/Button';
import { DataCollectionPageSection } from 'components/DataCollectionPageSection/DataCollectionPageSection';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import SVGInline from 'react-svg-inline';
import styles from './BankStatementFrame.css';
import IframeResizer from 'iframe-resizer-react';
import classNames from 'classnames';
import { postDataCollectionProcessEvent } from 'services/dataCollectionApi';
import {
  DATA_COLLECTION_EVENT,
  DATA_COLLECTION_PROCESS,
} from 'constants/dataCollection';

const bankStatementsUrl = window.LM_CONFIG.ILLION_BANK_STATEMENTS_URI || '';

const DataCollectionBankStatementFrame = ({
  applicationId,
  familyContactId,
  clientId,
  selectedProcesses,
  illionDocuments,
  advisor,
}) => {
  const [showIFrame, setShowIFrame] = useState(false);
  const isAllDocumentsNotCompleted = illionDocuments.every(
    (document) => !document.HasIllionDocument,
  );
  const isAllDocumentsCompleted = illionDocuments.every(
    (document) => document.HasIllionDocument,
  );
  const onConstructName = () => {
    let names = '';
    if (illionDocuments.length === 1) {
      names = illionDocuments[0].FirstName;
    } else if (illionDocuments.length === 2) {
      names = illionDocuments
        .map((document) => document.FirstName)
        .join(' and ');
    } else {
      names = illionDocuments
        .map((document, index) => {
          if (index === illionDocuments.length - 1) {
            return `and ${document.FirstName}`;
          }
          return document.FirstName;
        })
        .join(', ');
    }

    return names;
  };

  useEffect(() => {
    if (!bankStatementsUrl) {
      return;
    }

    const bankStatementsOrigin = new URL(bankStatementsUrl).origin;
    window.addEventListener('message', async (event) => {
      if (event.origin === bankStatementsOrigin) {
        const data = JSON.parse(event.data);
        if (data.event === 'submit_all') {
          try {
            await postDataCollectionProcessEvent(
              applicationId,
              DATA_COLLECTION_PROCESS.Illion,
              DATA_COLLECTION_EVENT.Begin,
            );
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
          }
        }
      }
    });
  }, [applicationId]);

  const renderIFrame = () => {
    const URL = bankStatementsUrl
      .replace('{LoanScenarioId}', applicationId)
      .replace('{FamilyContactId}', familyContactId)
      .replace('{ClientId}', clientId);

    return (
      <IframeResizer
        log
        src={URL}
        style={{ width: '1px', minWidth: '100%', border: 'none' }}
      />
    );
  };

  const renderSecurityPoints = () => (
    <ul className={styles.securityPoints}>
      <li>
        <SVGInline svg={LockIcon} width='30px' height='30px' />
        <div>We never store your login details</div>
      </li>
      <li>
        <SVGInline svg={EnergyIcon} width='30px' height='30px' />
        <div>Your data is fully encrypted</div>
      </li>
      <li>
        <SVGInline svg={ClockIcon} width='30px' height='30px' />
        <div>One-time only view of statements</div>
      </li>
      <li>
        <SVGInline svg={HeartIcon} width='30px' height='30px' />
        <div>Trusted by millions across Australia</div>
      </li>
    </ul>
  );

  const renderBankCard = () => (
    <div className={classNames(styles.bankCard)}>
      {showIFrame ? (
        renderIFrame()
      ) : (
        <>
          <h2>Connect to your banks</h2>
          <p>
            Log in to your banks and select the accounts required to share 180
            days of bank statements in a one-time view
          </p>
          <Button
            theme='buttonNext'
            className={styles.bankBtn}
            icon='sl-custom-dollar-currency-3'
            onClick={() => setShowIFrame(true)}
          >
            Connect to banks
          </Button>
        </>
      )}
    </div>
  );

  const renderStatusList = (document) => {
    if (document.HasIllionDocument) {
      return (
        <div className={styles.statusItem}>
          <SVGInline svg={CheckMark} className={styles.statusItemIcon} />
          <div className={styles.statusItemText}>
            Completed by {document.FirstName} {document.LastName}
          </div>
        </div>
      );
    }

    return (
      <div className={styles.statusItem}>
        <SVGInline svg={Pending} className={styles.statusItemIcon} />
        <div className={styles.statusItemText}>
          Not completed by {document.FirstName} {document.LastName}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.BankStatementFrameSection}>
      <div className={styles.statusSection}>
        {!isAllDocumentsCompleted && (
          <div className={styles.statusInfo}>
            {advisor.name} has requested bank statement from {onConstructName()}
          </div>
        )}

        <div className={styles.statusList}>
          {isAllDocumentsNotCompleted && (
            <div className={styles.statusItem}>
              <SVGInline svg={Pending} className={styles.statusItemIcon} />
              <div className={styles.statusItemText}>Please complete</div>
            </div>
          )}

          {isAllDocumentsCompleted && (
            <div className={styles.statusItem}>
              <SVGInline svg={CheckMark} className={styles.statusItemIcon} />
              <div className={styles.statusItemText}>
                Completed by {onConstructName()}
              </div>
            </div>
          )}

          {!isAllDocumentsNotCompleted &&
            !isAllDocumentsCompleted &&
            illionDocuments.map((document) => renderStatusList(document))}
        </div>
      </div>

      <DataCollectionPageSection
        id='dataCollectionBankStatement'
        customClassName={styles.supportingInformationSection}
        selectedProcesses={selectedProcesses}
      >
        <div className={classNames(styles.securityPoints)}>
          <h2>Securely provide your bank statements</h2>
          {renderSecurityPoints()}
        </div>
        {renderBankCard()}

        <p className={styles.footer}>
          This service is in partnership with our partners at illion Open
          Banking solutions.{' '}
          <a
            href='https://www.bankstatements.com.au/about/security'
            target='_blank'
            rel='noopener noreferrer'
          >
            Learn more
          </a>
          .
        </p>
      </DataCollectionPageSection>

      <div className={styles.footerSection}>
        <div className={styles.footerInfo}>
          <SVGInline svg={InfoAlert} className={styles.footerInfoIcon} />
          <div>
            <span className={styles.footerInfoTitle}>
              Why do I need to provide bank statements?
            </span>

            <p>
              Lenders require bank statements as supporting documents in order
              to assess your loan application and gain a full picture of your
              recent finances.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

DataCollectionBankStatementFrame.propTypes = {
  applicationId: PropTypes.number,
  familyContactId: PropTypes.number,
  clientId: PropTypes.number,
  selectedProcesses: PropTypes.arrayOf(PropTypes.string),
};

export default DataCollectionBankStatementFrame;
